import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Card, Typography, Tag, Spin } from "antd";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import api from "../interceptors/api";
import StandardButton from "../components/StandardButton";

const SITE_URL = "https://quto.dev";
const DEFAULT_OG_IMAGE = `${SITE_URL}/og-image.png`;

function formatBytes(size) {
  const n = Number(size) || 0;
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(2)} MB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

function formatDuration(seconds) {
  const s = Math.max(0, Math.floor(Number(seconds) || 0));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${String(r).padStart(2, "0")}`;
}

function formatResolution(w, h) {
  if (!w || !h) return null;
  return `${w}\u00d7${h}`;
}

function formatLocation(loc) {
  if (!loc) return null;
  const parts = [];
  if (loc.city) parts.push(loc.city);
  if (loc.country) parts.push(loc.country);
  return parts.length ? parts.join(", ") : null;
}

export default function SharedRecording() {
  const { slug } = useParams();
  const { t } = useTranslation();
  const [recording, setRecording] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [shortUrlRedirected, setShortUrlRedirected] = useState(false);

  useEffect(() => {
    if (!slug) return undefined;

    let cancelled = false;
    (async () => {
      try {
        const res = await api.get(`/screen-record/share/${encodeURIComponent(slug)}`);
        if (cancelled) return;
        setRecording(res.data);
      } catch (err) {
        if (cancelled) return;
        if (err?.response?.status === 404) {
          // /r/:slug is also used for the URL shortener — fall back to that
          // before declaring "not found", to preserve existing short links.
          try {
            const shortRes = await api.get(`/short-urls/${encodeURIComponent(slug)}`);
            if (cancelled) return;
            const original = shortRes?.data?.original_url;
            if (original) {
              setShortUrlRedirected(true);
              window.location.replace(original);
              return;
            }
          } catch (_) {
            if (cancelled) return;
          }
          setNotFound(true);
        } else {
          setNotFound(true);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [slug]);

  if (shortUrlRedirected) {
    return null;
  }

  if (loading) {
    return (
      <main className="max-w-3xl mx-auto p-6">
        <Card>
          <div className="text-center py-12">
            <Spin />
          </div>
        </Card>
      </main>
    );
  }

  if (notFound || !recording) {
    return (
      <main className="max-w-3xl mx-auto p-6">
        <Helmet>
          <title>Screen recording — Quto.dev</title>
          <meta name="robots" content="noindex,nofollow" />
        </Helmet>
        <Card>
          <div className="text-center py-12">
            <Typography.Title level={4}>
              {t("shared_recording_title")}
            </Typography.Title>
            <Typography.Paragraph type="secondary">
              {t("shared_recording_not_found")}
            </Typography.Paragraph>
            <StandardButton
              type="primary"
              size="middle"
              onClick={() => {
                window.location.href = "/";
              }}
            >
              Quto.dev
            </StandardButton>
          </div>
        </Card>
      </main>
    );
  }

  const {
    url,
    filename,
    size_bytes,
    mime_type,
    recording_duration,
    width,
    height,
    created_at,
    owner,
    location,
  } = recording;

  const dateText = created_at ? new Date(created_at).toLocaleString() : "";
  const durationText = formatDuration(recording_duration);
  const resolutionText = formatResolution(width, height);
  const sizeText = formatBytes(size_bytes);
  const locationText = formatLocation(location);
  const ogTitle = "Screen recording — Quto.dev";
  const ogDescription = dateText
    ? `Recorded on ${dateText}, ${durationText}`
    : `Screen recording shared via Quto.dev`;
  const canonical = typeof window !== "undefined"
    ? `${window.location.origin}/r/${slug}`
    : `${SITE_URL}/r/${slug}`;
  const videoMime = mime_type || "video/webm";

  return (
    <main className="max-w-3xl mx-auto p-6">
      <Helmet>
        <title>{ogTitle}</title>
        <meta name="description" content={ogDescription} />
        <meta name="robots" content="noindex,nofollow" />
        <link rel="canonical" href={canonical} />
        <meta property="og:site_name" content="Quto.dev" />
        <meta property="og:title" content={ogTitle} />
        <meta property="og:description" content={ogDescription} />
        <meta property="og:url" content={canonical} />
        <meta property="og:image" content={DEFAULT_OG_IMAGE} />
        {url && <meta property="og:type" content="video.other" />}
        {url && <meta property="og:video" content={url} />}
        {url && <meta property="og:video:secure_url" content={url} />}
        {url && <meta property="og:video:type" content={videoMime} />}
        {width && <meta property="og:video:width" content={String(width)} />}
        {height && <meta property="og:video:height" content={String(height)} />}
        <meta name="twitter:card" content={url ? "player" : "summary_large_image"} />
        <meta name="twitter:title" content={ogTitle} />
        <meta name="twitter:description" content={ogDescription} />
        <meta name="twitter:image" content={DEFAULT_OG_IMAGE} />
        {url && <meta name="twitter:player" content={canonical} />}
        {url && width && <meta name="twitter:player:width" content={String(width)} />}
        {url && height && <meta name="twitter:player:height" content={String(height)} />}
      </Helmet>

      <Card className="space-y-4">
        <Typography.Title level={3} className="!mb-2">
          {t("shared_recording_title")}
        </Typography.Title>

        {owner?.name && (
          <div className="flex items-center gap-3 mb-4">
            {owner.picture && (
              <img
                src={owner.picture}
                alt=""
                className="w-8 h-8 rounded-full object-cover"
                referrerPolicy="no-referrer"
              />
            )}
            <Typography.Text type="secondary">{owner.name}</Typography.Text>
          </div>
        )}

        <video
          src={url}
          controls
          playsInline
          className="w-full rounded bg-black"
          style={{ maxHeight: "70vh" }}
        />

        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-6">
          <div>
            <dt className="text-xs uppercase tracking-wide text-gray-500">
              {t("shared_recording_recorded_at")}
            </dt>
            <dd className="text-sm mt-1">{dateText || "—"}</dd>
          </div>
          <div>
            <dt className="text-xs uppercase tracking-wide text-gray-500">
              {t("shared_recording_duration")}
            </dt>
            <dd className="text-sm mt-1">{durationText}</dd>
          </div>
          {resolutionText && (
            <div>
              <dt className="text-xs uppercase tracking-wide text-gray-500">
                {t("shared_recording_resolution")}
              </dt>
              <dd className="text-sm mt-1">{resolutionText}</dd>
            </div>
          )}
          <div>
            <dt className="text-xs uppercase tracking-wide text-gray-500">
              {t("shared_recording_file_size")}
            </dt>
            <dd className="text-sm mt-1">{sizeText}</dd>
          </div>
          {locationText && (
            <div className="sm:col-span-2">
              <dt className="text-xs uppercase tracking-wide text-gray-500">
                {t("shared_recording_location")}
              </dt>
              <dd className="text-sm mt-1 flex items-center gap-2 flex-wrap">
                <span>{locationText}</span>
                <Tag color="blue">{t("shared_recording_location_owner_only")}</Tag>
              </dd>
            </div>
          )}
        </dl>

        <div className="mt-6 flex flex-wrap gap-2">
          {url && (
            <StandardButton
              type="primary"
              size="middle"
              onClick={() => window.open(url, "_blank", "noopener,noreferrer")}
            >
              {t("shared_recording_open_original")}
            </StandardButton>
          )}
          {filename && (
            <Typography.Text type="secondary" className="self-center text-xs">
              {filename}
            </Typography.Text>
          )}
        </div>
      </Card>
    </main>
  );
}
